// Textos e símbolos da mesa. Português do Brasil, frases curtas, vocabulário de bar.
import { DEFAULT_HOUSE_RULES, type Gesture, type HouseRules, SINAIS, type Seat, type SignalVisibility, type Suit, type Team, team } from '@truco6/regras';
import type { LugarPublico, Perfil } from '@truco6/mesa/protocolo';

export const PEDIDO: Record<number, string> = { 3: 'TRUCO', 6: 'SEIS', 9: 'NOVE', 12: 'DOZE' };
export const NAIPE_DA_MANILHA: Record<Suit, string> = { c: 'ZAP', h: 'COPAS', s: 'ESPADAS', d: 'OUROS' };
export const NOME_DO_NAIPE: Record<Suit, string> = { c: 'Zap', h: 'Copas', s: 'Espadas', d: 'Ouros' };
export const SIMBOLO: Record<Suit, string> = { c: '♣︎', h: '♥︎', s: '♠︎', d: '♦︎' };
// Sinais fixos (tabela de @truco6/regras): emoji, gesto e a carta que ele quer dizer.
export const GESTO_EMOJI = Object.fromEntries(SINAIS.map((s) => [s.gesture, s.emoji])) as Record<Gesture, string>;
export const GESTO_NOME = Object.fromEntries(SINAIS.map((s) => [s.gesture, s.gesto])) as Record<Gesture, string>;
export const CARTA_DO_SINAL = Object.fromEntries(SINAIS.map((s) => [s.gesture, s.carta])) as Record<Gesture, string>;
export const VISIBILIDADE_DOS_SINAIS: Record<SignalVisibility, string> = { desligados: 'Desligados', aviso: 'Só o aviso', todos: 'Todos veem' };
export const VOTO_EMOJI: Record<string, string> = { accept: '👍', run: '👎', raise: '🔥' };
export const REACOES = ['😂', '🔥', '👏', '😱', '🤡', '😎', '🙏', '💪'] as const;
export const OPINIOES = ['👍', '👎', '🔥'] as const;

// Nome de quem está no lugar: com bot jogando no lugar de quem saiu ou caiu, o nome do bot (Bot1, Bot2...).
export function apelidoDe(l: LugarPublico | null | undefined): string {
  if (!l) return '';
  return l.tipo === 'pessoa' && l.bot && l.botApelido ? l.botApelido : l.perfil.apelido;
}
export const ehBot = (l: LugarPublico | null | undefined): boolean => l?.tipo === 'bot' || (l?.tipo === 'pessoa' && l.bot);

// Quem assiste não é de trio nenhum: cada trio leva o apelido da primeira pessoa sentada nele, na ordem dos lugares
// ("Trio de Bia"). Trio só com bots leva o nome do primeiro bot; sem ninguém sentado, "Trio 1" ou "Trio 2".
export function trioDaPlateia(lugares: readonly (LugarPublico | null)[], t: Team): string {
  const doTrio = lugares.filter((l, i): l is LugarPublico => !!l && team(i as Seat) === t);
  const quem = doTrio.find((l) => !ehBot(l)) ?? doTrio[0];
  return quem ? `Trio de ${apelidoDe(quem)}` : `Trio ${t + 1}`;
}

// Vaga aberta: o bot joga no lugar e outra pessoa pode sentar (na sala valendo Tentos, nunca).
export const ehVaga = (l: LugarPublico | null | undefined): boolean => !!l && l.vagaAberta === true;
// Os lugares com vaga aberta, para sentar na primeira (lista de salas, chamado de amigo, quem assiste).
export const vagasAbertas = (lugares: readonly (LugarPublico | null)[]): Seat[] =>
  lugares.flatMap((l, i) => (ehVaga(l) ? [i as Seat] : []));
// Quem sentou na vaga e espera a próxima mão: o bot ainda joga no lugar.
export const esperaAMao = (l: LugarPublico | null | undefined): boolean => l?.tipo === 'pessoa' && l.entraNaMao !== undefined;

// O rosto que aparece no lugar: na vaga aberta e enquanto quem sentou espera a próxima mão, o do bot que joga.
export function rostoDoLugar(l: LugarPublico | null | undefined): { perfil: Perfil | undefined; usuario: string | undefined; bot: boolean } {
  if (l?.tipo === 'pessoa' && l.bot && (ehVaga(l) || esperaAMao(l))) return { perfil: { apelido: apelidoDe(l), avatar: null }, usuario: undefined, bot: true };
  return { perfil: l?.perfil, usuario: l?.tipo === 'pessoa' ? l.id : undefined, bot: ehBot(l) };
}

// Avisos da vaga aberta (canvas 5.3 e 5.3b). bot: o nome do bot do lugar (Bot1, Bot2...).
export const avisoDeQueda = (quem: string, bot: string) =>
  `${quem} caiu. O lugar dele fica guardado por 45\u00a0s. Depois, o ${bot} joga e a vaga abre para quem quiser sentar.`;
export const avisoDeVaga = (quem: string, bot: string, saiu: boolean) =>
  `${saiu ? quem + ' saiu.' : quem + ' não voltou em 45\u00a0s.'} O ${bot} joga no lugar e a vaga está aberta: quem entrar senta na próxima mão.`;

// Concordância: "Nós ganhamos", "Eles ganharam".
export function frase(trio: Team, meuTrio: Team, nos: string, eles: string): string {
  return trio === meuTrio ? 'Nós ' + nos : 'Eles ' + eles;
}
export function pontos(n: number): string { return n + (n === 1 ? ' ponto.' : ' pontos.'); }

export const REGRAS: string[] = [
  '<b>Manilha</b> é a carta seguinte à vira. Entre manilhas: Zap (paus), copas, espadas e ouros.',
  '<b>Truco</b> sobe a mão para 3, depois 6, 9 e 12. O trio decide por maioria, e quem não vota corre. Se a maioria correr e você votou aceitar, você decide se aceita mesmo assim.',
  '<b>Esconder</b>: ninguém esconde na primeira rodada. Na segunda e na terceira, todo mundo pode esconder, inclusive quem abre. Depois de uma rodada empachada, a seguinte é toda aberta.',
  '<b>Empachou</b> na primeira: vence quem ganhar a segunda. Empachou depois de uma rodada ganha: vale a primeira.',
  '<b>Mão de onze</b>: o trio com 11 vê as cartas do trio e decide se joga, valendo 3. <b>Mão de ferro</b>: os dois trios com 11, cartas abertas e sem truco. Quem vencer leva a partida.',
  '<b>Sinais</b> fixos, em qualquer mão: 😉 Zap, 🤨 Copas, 😗 Espadas, 😛 Ouros, 3️⃣ 3, 2️⃣ 2, 🅰️ A e 👎 nada.',
];

// O que a sala tem de diferente das regras padrão, para mostrar na espera: "30 s por jogada", "sem sinais".
export function regrasDaCasa(r: HouseRules): string[] {
  const d = DEFAULT_HOUSE_RULES, out: string[] = [];
  if (r.tempoPorJogadaMs !== d.tempoPorJogadaMs) out.push(`${Math.round(r.tempoPorJogadaMs / 1000)} s por jogada`);
  if (r.sinais !== d.sinais) out.push(r.sinais === 'desligados' ? 'sem sinais' : r.sinais === 'todos' ? 'sinais à vista de todos' : 'sinais só com aviso');
  if (r.vagasComBot !== d.vagasComBot) out.push(r.vagasComBot ? 'vagas com bot' : 'sem bot');
  if (r.zoeira === false) out.push('Zoeira desligada'); // ligada (o padrão) não precisa de linha
  return out;
}
